import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { v4 as uuid} from 'uuid';
import apiConfig from "@apiConfig";
import { addNotification } from "@actions/notifications.actions";
import http from "@core/http";
import { ExportTypeEnum } from "@models/ExportTypeEnum";
import { ID, ReportFileExtension } from "@models";
import { iNotification } from "@models/Noification";
import { iSearchResultsItem, ItemTypeEnum } from "@models/SearchResults";
import { currentCountrySelector } from "@selectors/countries.selector";
import { SearchResultItemTitle } from "./SearchResultItemTitle";
import { SearchAction, SearchResultItemActions } from "./SearchResultItemActions";
import {
    createFoodProfileActions,
    createSummaryReportActions,
    createCategoryReportActions,
} from "./searchResultActions.helpers";

interface SearchResultSectionListProps {
    list: iSearchResultsItem[];
    search: string;
    sectionId: ItemTypeEnum;
    isOpened: boolean;
    initialColumnsPerSection: number;
    onMoreResultsClick: () => void;
}

type ActionsByType = {
    [key in ItemTypeEnum]?: SearchAction[];
};

const COLUMN_COUNT = 4;
export const ITEMS_PER_COLUMN = 3;

const SearchResultSectionList: FCX<SearchResultSectionListProps> = ({
    list,
    search,
    sectionId,
    isOpened,
    initialColumnsPerSection,
    onMoreResultsClick,
}) => {
    const dispatch = useDispatch();
    const currentCountry = useSelector(currentCountrySelector);

    const initialItemsCount = initialColumnsPerSection * ITEMS_PER_COLUMN;

    const pushNotification = useCallback(
        (notification: iNotification) => dispatch(addNotification(notification)),
        [dispatch]
    );
    
    const downloadSummaryReport = useCallback(
        (reportId: ID, eTypeOfExport: ExportTypeEnum) => {
            const msgId = uuid();
            pushNotification({
                text: "Downloading...",
                id: msgId,
            });
            http.downloadFileXHRFromUrl(
                'GET',
                apiConfig.get('export report', { reportId, eTypeOfExport, countryCode: currentCountry })
            ).then(() => {
                pushNotification({
                    text: "Download was successful",
                    duration: 4000,
                    id: msgId,
                });
            });
        },
        [currentCountry, pushNotification]
    )

    const downloadCategoryReport = useCallback(
        (reportId: ID, eTypeOfExport: ReportFileExtension) => {
            const msgId = uuid();
            pushNotification({
                text: "Downloading...",
                id: msgId,
            });
            http.downloadFileXHRFromUrl(
                'GET',
                apiConfig.get('category report', { reportId, eTypeOfExport, countryCode: currentCountry })
            ).then(() => {
                pushNotification({
                    text: "Download was successful",
                    duration: 4000,
                    id: msgId,
                });
            });
        },
        [currentCountry, pushNotification]
    );

    const getActionsByType = useCallback((item: iSearchResultsItem): ActionsByType => ({
        [ItemTypeEnum.FoodProfile]: createFoodProfileActions(item),
        [ItemTypeEnum.Report]: createSummaryReportActions(item, downloadSummaryReport),
        [ItemTypeEnum.CategoryReport]: createCategoryReportActions(item, downloadCategoryReport),
    }), [downloadSummaryReport, downloadCategoryReport]);

    const resultItems = useMemo(() => (
        list.map((item) => {
            const actionsByType = getActionsByType(item);

            return {
                ...item,
                actions: actionsByType[sectionId] ?? undefined
            };
        })
    ), [list, sectionId, getActionsByType]);

    const visibleItems = useMemo(() => (
        resultItems.slice(0, isOpened ? Infinity : initialItemsCount)
    ), [resultItems, isOpened, initialItemsCount]);

    const visibleColumns = useMemo(() => {
        const columnCount = isOpened 
            ? COLUMN_COUNT 
            : Math.ceil(initialItemsCount / ITEMS_PER_COLUMN);
        
        return Array.from({ length: columnCount }, (_, colIdx) =>
            visibleItems.slice(colIdx * ITEMS_PER_COLUMN, (colIdx + 1) * ITEMS_PER_COLUMN)
        );
    }, [isOpened, visibleItems, initialItemsCount]);


    const showMoreResults = resultItems.length > ITEMS_PER_COLUMN && initialColumnsPerSection !== COLUMN_COUNT;

    if (!visibleItems.length) return null;

    return (
        <>
            <div className="ToolSearch__sectionList">
                {visibleColumns.map((col, colIdx) => (
                    <div className="ToolSearch__sectionListColumn" key={colIdx}>
                        {col.map((item, rowIdx) => (
                            <div className="ToolSearch__item" key={item.id}>
                                <SearchResultItemTitle
                                    item={item}
                                    index={colIdx * 3 + rowIdx}
                                    sectionId={sectionId}
                                    search={search}
                                />
                                <SearchResultItemActions actions={item.actions} />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            {showMoreResults && (
                <div
                    className={cn(
                        'ToolSearch__moreResults',
                        isOpened ? 'ToolSearch__moreResults--opened' : ''
                    )}
                >
                    <span
                        className="ToolSearch__moreResultsButton"
                        data-testid="buttonHeaderSearchCategoryMoreResults"
                        onClick={onMoreResultsClick}
                    >
                        {isOpened ? 'hide' : 'more'}
                    </span>
                </div>
            )}
        </>
    );
};

export default SearchResultSectionList;
